import React from "react";
import { cn } from "../../../lib/utils";

export type GlassVariant = "bar" | "panel" | "modal" | "chip";

export interface GlassSurfaceProps
  extends React.HTMLAttributes<HTMLElement> {
  /** Which glass shape/role to use. Defaults to "panel". */
  variant?: GlassVariant;
  /** Element to render. Defaults to "div". */
  as?: keyof JSX.IntrinsicElements;
  /** Enable the edge-refraction filter (subtle backdrop displacement). */
  refract?: boolean;
  /** Brighter ice rim — use for active/selected/featured surfaces. */
  active?: boolean;
  /** Add the frozen frost grain texture (heroes / large panels). */
  frost?: boolean;
  /** Slow living-light sheen drift (auto-disabled under reduced-motion). */
  alive?: boolean;
}

/**
 * GlassSurface — the reusable CSS "ice glass" surface used across the app
 * and as the fallback for <LiquidGlass> when WebGL isn't available.
 *
 * It's a thin wrapper that composes the `.bb-glass*` classes from
 * styles/liquid-glass.css. All visual tuning lives in CSS tokens, so
 * dialing in the look later is a stylesheet edit, not a component edit.
 *
 *   <GlassSurface variant="modal" refract>…</GlassSurface>
 *   <GlassSurface as="header" variant="bar">…</GlassSurface>
 */
const GlassSurface = React.forwardRef<HTMLElement, GlassSurfaceProps>(
  (
    {
      variant = "panel",
      as = "div",
      refract = false,
      active = false,
      frost = false,
      alive = false,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    const Tag = as as React.ElementType;
    return (
      <Tag
        ref={ref}
        className={cn(
          "bb-glass",
          `bb-glass--${variant}`,
          refract && "bb-glass--refract",
          active && "bb-glass--rim-strong",
          frost && "bb-glass--frost",
          alive && "bb-glass--alive",
          className
        )}
        {...rest}
      >
        {children}
      </Tag>
    );
  }
);

GlassSurface.displayName = "GlassSurface";

export default GlassSurface;
